import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Footprints, Leaf, Sparkles, Waves, Wind } from "lucide-react";
import { IMAGES } from "@/data/clinic";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

const SERVICES = [
    {
        name: "Ancient Marma Chikitsa",
        icon: Leaf,
        image: IMAGES.marmaTherapy,
        span: "md:col-span-4",
        text: "Our signature practice — gentle, precise touch on the body's traditional vital points, drawn from classical Ayurvedic knowledge and personalized to you.",
    },
    {
        name: "Body Massage",
        icon: Droplets,
        image: IMAGES.essentialOils,
        span: "md:col-span-2",
        text: "Full-body traditional massage, often with warm herbal oils in the Abhyanga tradition.",
    },
    {
        name: "Foot Massage",
        icon: Footprints,
        image: IMAGES.reflexology,
        span: "md:col-span-2",
        text: "Traditional foot work with unhurried attention to the soles — sought for deep rest.",
    },
    {
        name: "Head Massage",
        icon: Sparkles,
        span: "md:col-span-2",
        text: "Calming traditional head massage — a simple, loved way to quiet a busy mind.",
    },
    {
        name: "Cupping",
        icon: Waves,
        span: "md:col-span-2",
        text: "Traditional cupping bodywork, offered as a wellness practice and adapted to your comfort.",
    },
    {
        name: "Scraping",
        icon: Wind,
        span: "md:col-span-2",
        text: "Traditional scraping techniques, offered as supportive wellness bodywork within your session.",
    },
];

const Card = ({ s, i }) => {
    const inner = s.image ? (
        <>
            <img
                src={s.image}
                alt={s.name}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
            <div className="relative flex h-full flex-col justify-end p-8">
                <h3 className="font-serif text-2xl font-medium text-cream md:text-3xl">{s.name}</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-cream/80">{s.text}</p>
            </div>
        </>
    ) : (
        <div className="flex h-full flex-col justify-between p-8">
            <span className="inline-flex w-fit rounded-2xl bg-forest/10 p-3 text-forest transition-colors duration-500 group-hover:bg-forest group-hover:text-cream">
                <s.icon className="h-5 w-5" />
            </span>
            <div className="mt-10">
                <h3 className="font-serif text-2xl font-medium text-ink">{s.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-moss">{s.text}</p>
            </div>
        </div>
    );

    return (
        <Reveal delay={(i % 3) * 0.07} className={s.span}>
            <div
                data-testid={`service-card-${i}`}
                className={`group relative h-full min-h-[220px] overflow-hidden rounded-3xl border border-sand transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(28,35,33,0.3)] ${
                    s.image ? "bg-ink" : "bg-cream"
                }`}
            >
                {inner}
            </div>
        </Reveal>
    );
};

export default function Therapies() {
    return (
        <section id="therapies" data-testid="therapies-section" className="bg-bone py-20 md:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    overline="Traditional Wellness & Bodywork"
                    title="The Services We Offer"
                    testId="therapies-heading"
                >
                    Ancient Marma Chikitsa and traditional hands-on bodywork in Kurnool — every
                    session personalized to your concerns, your comfort and your pace.
                </SectionHeading>

                <div className="mt-14 grid gap-6 md:grid-cols-6">
                    {SERVICES.map((s, i) => (
                        <Card key={s.name} s={s} i={i} />
                    ))}
                    <Reveal delay={0.14} className="md:col-span-4">
                        <div className="flex h-full min-h-[220px] flex-col justify-between rounded-3xl bg-forest p-8 text-cream">
                            <p className="max-w-lg font-serif text-2xl font-medium leading-snug md:text-3xl">
                                Every session begins with your concerns — and is shaped around them.
                            </p>
                            <Link
                                to="/conditions-and-concerns"
                                data-testid="therapies-concerns-link"
                                className="group mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-ink transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold"
                            >
                                See the concerns we work with
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
